import type { ChartToSign, PendingCharts, PendingReviewChart } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { errorMessage, get } from "../api";
import { Alert, Badge, Loading, PageHeader, Panel, PanelHeader, formatDateTime24 } from "../components/ui";
import { useSession } from "../session";
import { dayLabel } from "./Calendar";

/** Charts to sign and charts waiting for the medical director (also the menu's count: only your own). */
export function usePendingCharts() {
  const { can } = useSession();
  return useQuery({ queryKey: ["charts-pending"], queryFn: () => get<PendingCharts>("/api/charts/pending"), enabled: can("charts.write", "charts.sign", "charts.view_all"), refetchInterval: 120_000 });
}

const day = (d: string) => dayLabel(d, { month: "short", day: "numeric", year: "numeric" });
const daysSince = (iso: string) => Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 86_400_000));

/** Pending Charts: your charts to sign first; then, collapsed, what's waiting for the medical director. */
export function PendingChartsPage() {
  const { can } = useSession();
  const list = usePendingCharts();
  const d = list.data;
  // Everyone else's charts to sign, by who has to sign them (managers).
  const byStaff = new Map<string, ChartToSign[]>();
  for (const c of d?.others ?? []) byStaff.set(c.staffName || "No nurse assigned", [...(byStaff.get(c.staffName || "No nurse assigned") ?? []), c]);
  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <PageHeader title="Pending Charts" description="Charts still to sign, and signed charts waiting for the medical director's review. Which kinds of chart need review is set in Settings → Forms and charts." />
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : (
        d && (
          <>
            {d.toReview.length > 0 && (
              <Panel className="overflow-x-auto">
                <PanelHeader title={`To review (${d.toReview.length})`} description="Signed charts waiting for your review, oldest first." />
                <ReviewTable rows={d.toReview} />
              </Panel>
            )}
            {(d.mine.length > 0 || can("charts.write")) && (
              <Panel className="overflow-x-auto">
                <PanelHeader title={`Your charts to sign (${d.mine.length})`} description="Visits that are done (or from an earlier day) whose chart isn't signed yet, oldest first." />
                {d.mine.length ? <SignTable rows={d.mine} /> : <p className="px-4 pb-4 text-sm text-muted">Nothing to sign. You're all caught up.</p>}
              </Panel>
            )}
            {[...byStaff].map(([name, rows]) => (
              <Panel key={name} className="overflow-x-auto">
                <PanelHeader title={`${name}: to sign (${rows.length})`} />
                <SignTable rows={rows} />
              </Panel>
            ))}
            {d.waiting.length > 0 && (
              <Panel>
                <details>
                  <summary className="cursor-pointer px-4 py-3 font-semibold">Waiting for the medical director ({d.waiting.length})</summary>
                  <div className="overflow-x-auto">
                    <ReviewTable rows={d.waiting} />
                  </div>
                </details>
              </Panel>
            )}
            {can("charts.sign") && !d.toReview.length && <Alert tone="info">Nothing waiting for your review.</Alert>}
          </>
        )
      )}
    </div>
  );
}

function SignTable({ rows }: { rows: ChartToSign[] }) {
  return (
    <table className="table-base">
      <thead>
        <tr>
          <th>Client</th>
          <th>Chart</th>
          <th>Visit</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.chartId ?? c.appointmentId} className="hover:bg-mist/40">
            <td>
              <Link to={c.chartId ? `/charts/${c.chartId}${c.notCharted ? "?update=1" : ""}` : `/visits/${c.appointmentId}`} className="font-semibold hover:text-brand">
                {c.clientName}
              </Link>
            </td>
            <td>
              {c.chartId && !c.notCharted ? c.name : <Badge tone="wait">{c.name}</Badge>}
              <div className="text-xs text-muted">{c.notCharted ? `Added at checkout: ${c.notCharted.join(", ")}` : c.serviceName}</div>
            </td>
            <td className="whitespace-nowrap">{day(c.date)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReviewTable({ rows }: { rows: PendingReviewChart[] }) {
  return (
    <table className="table-base">
      <thead>
        <tr>
          <th>Client</th>
          <th>Chart</th>
          <th>Visit</th>
          <th>Nurse</th>
          <th>Signed</th>
          <th>Waiting</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id} className="hover:bg-mist/40">
            <td>
              <Link to={c.source ? `/records/${c.id}` : `/charts/${c.id}`} className="font-semibold hover:text-brand">
                {c.clientName}
              </Link>
            </td>
            <td>
              {c.name}
              <div className="text-xs text-muted">{c.serviceName}</div>
            </td>
            <td className="whitespace-nowrap">{c.date ? day(c.date) : ""}</td>
            <td>{c.nurse}</td>
            <td className="whitespace-nowrap">{formatDateTime24(c.signedAt)}</td>
            <td className="whitespace-nowrap">{daysSince(c.signedAt) === 0 ? "Today" : `${daysSince(c.signedAt)} day${daysSince(c.signedAt) === 1 ? "" : "s"}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
